import { useNavigate } from 'react-router-dom';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { BewerberStatus, BewerberStatusConfig, Bewerber } from '../../types/bewerber';
import { KanbanCard } from './KanbanCard';

interface KanbanColumnProps {
  /** Droppable id; the status itself, except for the three Einstellen columns. */
  columnKey: string;
  status: BewerberStatus;
  /** Overrides the status label (the Einstellen columns share one status). */
  label?: string;
  /** Long description, shown as a tooltip on the header. */
  title?: string;
  bewerbers: Bewerber[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  totalCount?: number; // For VERLOREN: total count when limited display
  /** Oldest card at the top instead of the newest. */
  sortReversed?: boolean;
  /** Only columns that offer the switch pass this; without it no button is shown. */
  onToggleSort?: () => void;
}

export function KanbanColumn({
  columnKey,
  status,
  label,
  title,
  bewerbers,
  isCollapsed,
  onToggleCollapse,
  totalCount,
  sortReversed = false,
  onToggleSort,
}: KanbanColumnProps) {
  const navigate = useNavigate();
  const config = BewerberStatusConfig[status];
  // Eine Spalte traegt ihre Ueberschrift auf 220px und, eingeklappt, senkrecht.
  // Wo dafuer ein eigener Zuschnitt hinterlegt ist, gilt der.
  const columnLabel = label ?? config.kanbanLabel ?? config.label;
  const { setNodeRef, isOver } = useDroppable({
    id: columnKey,
    data: {
      type: 'column',
      status,
    },
  });

  const bewerberIds = bewerbers.map((b) => b.id);

  // Display count: use totalCount if provided (for VERLOREN), otherwise bewerbers.length
  const displayCount = totalCount ?? bewerbers.length;
  const hasMore = totalCount !== undefined && totalCount > bewerbers.length;

  // Collapsed view - narrow vertical strip
  if (isCollapsed) {
    return (
      <div
        data-testid={`spalte-${columnKey}`}
        ref={setNodeRef}
        onClick={onToggleCollapse}
        title={title}
        className={`
          w-10 flex-shrink-0 bg-gray-50 rounded-lg cursor-pointer
          hover:bg-gray-100 flex flex-col items-center py-3
          transition-all duration-200
          min-h-[200px] max-h-[calc(100vh-280px)] lg:max-h-full
          ${isOver ? 'bg-primary-50 ring-2 ring-primary-300' : ''}
        `}
      >
        {/* Count badge at top */}
        <span className={`inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
          {displayCount}
        </span>

        {/* Rotated label - top aligned, horizontally centered */}
        <div className="mt-3">
          <span
            className={`text-sm font-medium ${config.color} whitespace-nowrap`}
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {columnLabel}
          </span>
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div
      data-testid={`spalte-${columnKey}`}
      className="flex flex-col bg-gray-50 rounded-lg min-w-[220px] max-w-[280px] flex-shrink-0"
    >
      {/* Column Header - clickable to collapse */}
      <div
        className={`px-3 py-2 rounded-t-lg ${config.bgColor} cursor-pointer hover:opacity-90 transition-opacity`}
        onClick={onToggleCollapse}
        title={title}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <svg className={`h-4 w-4 ${config.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <h3 className={`text-sm font-semibold ${config.color}`}>
              {columnLabel}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            {onToggleSort && (
              <button
                type="button"
                data-testid={`sortierung-${columnKey}`}
                aria-pressed={sortReversed}
                onClick={(e) => {
                  // The header collapses the column on click — this one only flips the order.
                  e.stopPropagation();
                  onToggleSort();
                }}
                title={sortReversed ? 'Älteste oben – klicken für neueste oben' : 'Neueste oben – klicken für älteste oben'}
                aria-label={sortReversed ? 'Sortierung: älteste oben' : 'Sortierung: neueste oben'}
                className={`inline-flex items-center justify-center h-5 w-5 rounded ${config.color} ${
                  sortReversed ? 'bg-white/70 ring-1 ring-current' : 'opacity-60 hover:opacity-100 hover:bg-white/50'
                }`}
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {sortReversed ? (
                    // Arrow up: the oldest card leads
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                  ) : (
                    // Arrow down: the newest card leads (default)
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                  )}
                </svg>
              </button>
            )}
            <span className={`inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
              {displayCount}
            </span>
          </div>
        </div>
      </div>

      {/* Column Content */}
      <div
        ref={setNodeRef}
        className={`
          flex-1 p-2 overflow-y-auto min-h-[200px] max-h-[calc(100vh-280px)] lg:max-h-full
          transition-colors duration-200
          ${isOver ? 'bg-primary-50 ring-2 ring-primary-300 ring-inset' : ''}
        `}
      >
        <SortableContext items={bewerberIds} strategy={verticalListSortingStrategy}>
          {bewerbers.length > 0 ? (
            bewerbers.map((bewerber) => (
              <KanbanCard key={bewerber.id} bewerber={bewerber} />
            ))
          ) : (
            <div className="flex items-center justify-center h-20 text-gray-400 text-sm">
              <div className="text-center">
                <svg className="mx-auto h-8 w-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p>Keine Bewerber</p>
              </div>
            </div>
          )}
        </SortableContext>

        {/* "View all" link for VERLOREN when there are more items */}
        {hasMore && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              navigate('/bewerber?status=VERLOREN');
            }}
            className="block w-full mt-2 px-2 py-1.5 text-center text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
          >
            Alle {displayCount} anzeigen →
          </button>
        )}
      </div>
    </div>
  );
}
